import { useEffect, useMemo, useState } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery } from '@zenbusiness/application-commons-graphql';
import DialogContent from '@zenbusiness/zui/DialogContent';
import Heading from '@zenbusiness/zui/Heading';
import LinearProgress from '@zenbusiness/zui/LinearProgress';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Radio from '@zenbusiness/zui/Radio';
import RadioGroup from '@zenbusiness/zui/RadioGroup';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid, ActionTwoButtonsGrid } from '#client/components/Grid';
import { CARD_STATUS } from '#client/constants/cards';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFreezeCard from '#client/hooks/useFreezeCard';
import { useLogError } from '#client/hooks/useLogError';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { GET_CARD_OPTIONS_QUERY } from '#client/queries';

import { BUSINESS_DEBIT_CARD } from './constants';
import { BoxContentStyled, FormControlLabelStyled, Wrapper } from './styles';

export default function FreezeCardForm({ handleClose }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [idCardSelected, setIdCardSelected] = useState();
  const { unitAccountId } = useUnitAccountId();
  const { createCommonErrorToast } = useToastContext();
  const freezeButtonId = useEnterSubmit();

  // get list card active
  const { loading, data, error, errors } = useQuery(GET_CARD_OPTIONS_QUERY, {
    variables: {
      params: {
        filter: {
          status: [CARD_STATUS.ACTIVE]
        },
        identifier: {
          businessUuid: businessEntityUuid,
          unitAccountId
        }
      }
    },
    noThrow: true,
    skipCache: true
  });

  useLogError(errors, error);
  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const cards = useMemo(() => data?.cards?.cards ?? [], [data]);

  // freeze card
  const { handleFreezeCard, loading: freezeCardLoading } = useFreezeCard(
    idCardSelected,
    handleClose
  );

  const defaultCardID = cards[0]?.id;

  const buttonPressedTracker = useButtonPressedCallback({
    elementName: 'confirm_freeze_dc',
    elementText: 'Freeze card',
    appPageName: 'freeze_card_modal'
  });

  const freezeCardHandler = () => {
    buttonPressedTracker();
    handleFreezeCard();
  };

  const handleSelectCard = (e) => {
    setIdCardSelected(e.target.value);
  };

  useEffect(() => {
    if (!loading && defaultCardID) {
      setIdCardSelected(defaultCardID);
    }
  }, [defaultCardID, loading]);

  return (
    <>
      <DialogContent>
        <ContainerGrid>
          <FocusGrid>
            <Wrapper spacing="32px">
              <Stack spacing="16px">
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Which card do you need to freeze?
                </Heading>
                <Text
                  color="secondary"
                  size="lg"
                >
                  Freezing a card means that any recurring charges on this card will fail. To
                  unfreeze your card any time, go to Banking in your dashboard.
                </Text>
              </Stack>
              <BoxContentStyled>
                {!!loading && <LinearProgress />}
                {!loading && defaultCardID && (
                  <RadioGroup
                    aria-label="Choose a card you would like to freeze."
                    defaultValue={defaultCardID}
                    name="radio-buttons-group"
                    onChange={handleSelectCard}
                  >
                    {cards.map(({ id, last4Digits, type }) => (
                      <FormControlLabelStyled
                        control={
                          <Radio
                            inputProps={{
                              'aria-label': id
                            }}
                          />
                        }
                        key={last4Digits}
                        label={
                          type === BUSINESS_DEBIT_CARD ? (
                            <Text
                              size="xl"
                              weight="medium"
                            >{`Physical card ending in ${last4Digits}`}</Text>
                          ) : (
                            <Text
                              size="xl"
                              weight="medium"
                            >{`Virtual card ending in ${last4Digits}`}</Text>
                          )
                        }
                        value={id}
                      />
                    ))}
                  </RadioGroup>
                )}
              </BoxContentStyled>
            </Wrapper>
          </FocusGrid>
        </ContainerGrid>
      </DialogContent>
      <ActionTwoButtonsGrid
        firstButton={
          <LoadingButton
            disabled={freezeCardLoading}
            fullWidth
            onClick={handleClose}
            size="large"
            variant="outlined"
          >
            Never mind
          </LoadingButton>
        }
        secondButton={
          <LoadingButton
            color="error"
            disabled={freezeCardLoading || loading}
            fullWidth
            id={freezeButtonId}
            loading={freezeCardLoading}
            onClick={freezeCardHandler}
            size="large"
            variant="contained"
          >
            Freeze card
          </LoadingButton>
        }
      />
    </>
  );
}

FreezeCardForm.propTypes = {
  handleClose: PropTypes.func.isRequired
};
